/**
 * Empty, loading and error states.
 *
 * Every list and panel in the product uses these, so a screen is never blank
 * and an error always says what happened, why, and what to do next.
 */
import type { ReactNode } from "react";

import { ApiError } from "@/lib/api";
import { Icon, type IconName } from "@/components/Icon";

export function EmptyState({
  title,
  message,
  action,
  icon = "list",
}: {
  title: string;
  message: string;
  action?: ReactNode;
  icon?: IconName;
}) {
  return (
    <div className="empty">
      <span style={{ color: "var(--ink-tertiary)" }}>
        <Icon name={icon} size={28} />
      </span>
      <div className="stack" style={{ gap: "var(--space-2)", alignItems: "center" }}>
        <p className="t-h3">{title}</p>
        <p className="t-body empty__text">{message}</p>
      </div>
      {action}
    </div>
  );
}

/** Skeletons match the final geometry, so nothing jumps when data lands. */
export function SkeletonRows({ rows = 5, height = 56 }: { rows?: number; height?: number }) {
  return (
    <div className="stack" style={{ gap: "var(--space-2)", padding: "var(--space-4)" }}>
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="skeleton" style={{ height }} />
      ))}
    </div>
  );
}

export function SkeletonCards({ count = 6 }: { count?: number }) {
  return (
    <div className="room-grid">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="skeleton" style={{ height: 148 }} />
      ))}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
  compact = false,
}: {
  error: unknown;
  onRetry?: () => void;
  compact?: boolean;
}) {
  const apiError = error instanceof ApiError ? error : null;
  const offline = apiError?.isOffline;
  const title = offline
    ? "You are offline"
    : apiError?.isPermissionDenied
      ? "You do not have access to this"
      : "This could not be loaded";
  const message =
    apiError?.message ??
    "Something went wrong while loading this. Try again in a moment.";

  return (
    <div className={compact ? "notice notice--error" : "empty"}>
      {compact ? (
        <>
          <span className="notice__bar" aria-hidden="true" />
          <div className="stack" style={{ gap: 4 }}>
            <p className="notice__title">{title}</p>
            <p className="notice__body">{message}</p>
            {apiError?.requestId && (
              <p className="t-mono t-caption">Reference {apiError.requestId}</p>
            )}
          </div>
        </>
      ) : (
        <>
          <span style={{ color: "var(--status-occupied)" }}>
            <Icon name={offline ? "wifiOff" : "warning"} size={28} />
          </span>
          <div className="stack" style={{ gap: "var(--space-2)", alignItems: "center" }}>
            <p className="t-h3">{title}</p>
            <p className="t-body empty__text">{message}</p>
            {apiError?.requestId && (
              <p className="t-mono t-caption ink-tertiary">
                Reference {apiError.requestId}
              </p>
            )}
          </div>
          {onRetry && (
            <button type="button" className="btn btn--secondary" onClick={onRetry}>
              <Icon name="refresh" size={16} />
              Try again
            </button>
          )}
        </>
      )}
    </div>
  );
}

export function Notice({
  tone,
  title,
  children,
  action,
}: {
  tone: "error" | "warning" | "success" | "info";
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={`notice notice--${tone}`}>
      <span className="notice__bar" aria-hidden="true" />
      <div className="stack grow" style={{ gap: 4 }}>
        <p className="notice__title">{title}</p>
        {children && <div className="notice__body">{children}</div>}
      </div>
      {action}
    </div>
  );
}
